import { VariantPrice } from '@fdc/__generated__/graphql';

import { LabelType } from './product-tile-types';

export const formatPrice = (price?: number) =>
  price ? `$${price.toFixed(2)}` : undefined;

const eaCupSuffix = ' (approx)';
const formatCupPrice = ({
  price,
  unit,
  suffix,
  UOMSize,
}: {
  price?: number;
  unit?: string;
  suffix?: string;
  UOMSize?: number;
}) =>
  price && unit
    ? `${formatPrice(price)} / ${UOMSize ?? ''}${unit.toLowerCase()}${
        suffix ?? ''
      }`
    : undefined;

/**
 * Member-price decision inputs ride the GraphQL `Any` scalar, so the fields the
 * pricing maths needs are declared here rather than generated.
 */
export type MemberPriceTag = {
  promotionalPrice: number;
  promotionalComparativePrice: number;
  promotionalComparativeUom: string;
  promotionalComparativeSize: number;
  promotionalAvgComparativePrice: number;
  promotionalAvgComparativeSize: number;
  promotionalAvgComparativeUom: string;
};

export const getMemberPricing = (memberPrice: MemberPriceTag) => {
  if (!memberPrice) {
    return {
      price: undefined,
      cupPriceWeight: undefined,
      cupPriceEach: undefined,
    };
  }

  const price = memberPrice.promotionalPrice / 100;

  const weightCupPrice = formatCupPrice({
    price: memberPrice.promotionalComparativePrice / 100,
    unit: memberPrice.promotionalComparativeUom,
    suffix: '',
    UOMSize: memberPrice.promotionalComparativeSize,
  });

  const eachCupPrice = formatCupPrice({
    price: memberPrice.promotionalAvgComparativePrice / 100,
    unit: memberPrice.promotionalAvgComparativeUom,
    suffix: eaCupSuffix,
    UOMSize: memberPrice.promotionalAvgComparativeSize,
  });

  return {
    price,
    cupPriceWeight: weightCupPrice ?? undefined,
    cupPriceEach: eachCupPrice ?? undefined,
  };
};

export const getCupAndMemberPricing = ({
  memberTag,
  productWeightVariant,
  productEachVariant,
  basePrice,
}: {
  memberTag: MemberPriceTag | undefined;
  productWeightVariant: VariantPrice;
  productEachVariant: VariantPrice;
  basePrice: number;
}) => {
  const {
    price: memberPrice,
    cupPriceWeight: memberWeightCupPrice,
    cupPriceEach: memberEachCupPrice,
  } = getMemberPricing(memberTag);

  const weightCupPrice = memberPrice
    ? (memberWeightCupPrice ?? undefined)
    : formatCupPrice({
        price: productWeightVariant?.cupPrice,
        unit: productWeightVariant?.cupUnit,
      });

  const eachCupPrice = memberPrice
    ? (memberEachCupPrice ?? undefined)
    : formatCupPrice({
        price: productEachVariant?.cupPrice,
        unit: productEachVariant?.cupUnit,
        suffix: productWeightVariant ? eaCupSuffix : undefined,
      });

  const nonMemberPrice = basePrice;
  const nonMemberCupPrice = productWeightVariant
    ? formatCupPrice({
        price: productWeightVariant?.cupPrice,
        unit: productWeightVariant?.cupUnit,
      })
    : formatCupPrice({
        price: productEachVariant?.cupPrice,
        unit: productEachVariant?.cupUnit,
      });

  let labelType: 'special' | 'memberPrice' | undefined;
  if ((productWeightVariant ?? productEachVariant)?.isSpecial) {
    labelType = 'special';
  } else if (memberTag) {
    labelType = 'memberPrice';
  }

  return {
    memberPrice,
    weightCupPrice,
    eachCupPrice,
    nonMemberPrice,
    nonMemberCupPrice,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: refactor to remove type assertion
    labelType: labelType as LabelType,
  };
};
